import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../api/axios';
import { toast } from 'react-toastify';
import useAuth from './useAuth';

export const clientAIModels = ['traditional_search'] as const;

export const clientQueryKeys = {
  listClients: ['ListClients'] as const,
  listClientSummaries: ['ListClientSummaries'] as const,
  listClientMinimals: ['ListClientMinimals'] as const,
  allClientSummaries: ['AllClientSummaries'] as const,
  productTemplates: ['ProductTemplates'] as const,
  incentiveOrganizations: ['IncentiveOrganizations'] as const,
};

export interface Client {
  accept_policy_description?: string | null;
  ai_model: (typeof clientAIModels)[number];
  allowed_listener_tags?: number[];
  archived_at: any;
  benefits_description?: string;
  benefits_description_2?: string;
  client_code?: string;
  comms_configuration?: CommsConfiguration;
  comms_logo_file_url?: string;
  contract_date?: string;
  contract_includes_comms: boolean;
  created_at: string;
  crm_sync: boolean;
  description: string;
  eligibility_config: EligibilityConfig;
  enabled_resource_ids: number[];
  go_live_date?: string;
  group_banner_enabled: boolean;
  group_banner_file_url?: string | null;
  group_id: any;
  id?: number;
  vertical: Vertical;
  domain_id?: number;
  domain?: Domain;
  invoice_terms: any;
  is_test: boolean;
  landing_page_content?: string;
  landing_page_logo_file_path?: string;
  landing_page_logo_file_url?: string;
  landing_page_message?: string;
  learn_more_redirect_url?: string;
  logo_background_file_url?: string;
  logo_file_url?: string;
  logo_overlay_file_url?: string;
  member_self_enrollment: boolean;
  monthly_cap: any;
  name: string;
  notes?: string;
  packages: Package[];
  peer_onboarding_tier_id?: number | null;
  per_member_rate: any;
  powered_by_header_enabled: boolean;
  powered_by_header_file_url?: string;
  promo_configuration?: PromoConfiguration;
  quickbooks_customer_id: any;
  reporting_configuration: any;
  reseller?: string;
  retired_at: any;
  schedule_frequency?: string;
  schedule_ordinal?: number;
  schedule_hour?: number;
  sso_configuration?: SsoConfiguration | null;
  status: string;
  theme: JSON;
  trusted_sso: boolean;
  use_global_resources?: boolean;
  use_mfa?: boolean;
  listener_scope?: 'global' | 'group';
  private_peer_content?: boolean;
  content_optional?: boolean;
  video_configuration?: VideoConfiguration;
  welcome_video_vimeo_id?: string;
  check_in_message?: string;
  check_in_frequency?: number;
  crisis_resource_id?: number | null;
}

export interface PackageSummary {
  id: number;
  name: string;
  code: string;
}

export interface ClientSummary {
  contract_date: Date;
  id: number;
  is_test_client: boolean;
  name: string;
  packages: PackageSummary[];
  reseller: string;
  status: string;
  total_eligible_members: number;
  total_active_members: number;
}

export interface EligibilityConfig {
  default_package?: number;
  hide_client_admin_eligibility?: boolean;
  transfer_type?: string;
}

export interface PromoConfiguration {
  button_label?: string;
  enabled?: boolean;
  image_file?: any;
  image_url?: string;
  placements?: string[];
  type: string;
  url?: string;
  video_file?: any;
  video_url?: string;
}

export interface SsoConfiguration {
  allow_sign_out?: boolean;
  collect_password?: boolean;
  sso_error_action?: string;
  allow_signup_exit?: boolean;
  authentication_url?: any;
  allow_invitation_expired_recovery?: boolean;
}

export interface Package {
  archived_at: any;
  annual_platform_access_fee: number;
  benefits_description?: string;
  benefits_description_col_1?: string;
  benefits_description_col_2?: string;
  branding_configuration?: BrandingConfiguration;
  can_manage_dependents: boolean;
  client_id?: number;
  client_cost_per_minute: number;
  client_cost_per_call_unit: number;
  client_fee_per_member_per_month: number;
  code: string;
  collect_user_address: boolean;
  cost_per_minute_cents: number;
  created_at: string;
  dependents_configuration: DependentsConfiguration;
  domain_whitelist?: string;
  external_plan_id?: number | null;
  force_web_app?: boolean;
  id?: number;
  member_count: number;
  monthly_platform_access_fee: number;
  my_plan_file_url?: string | null;
  name: string;
  package_products: PackageProduct[];
  subscriptions: Subscription[];
  theme_name?: string;
  web_app_url?: string;
}

export interface PackageProduct {
  configuration: Configuration;
  created_at: string;
  id: number;
  minute_balance: number;
  package_id: number;
  product_id: number;
  product: Product;
  status: string;
  unused_minute_balance: number;
}

export interface Configuration {
  additional_discount?: any;
  auto_renew_frequency?: string;
  auto_renew_maximum_uses?: number;
  auto_renew?: boolean;
  cadence_time?: string;
  cadence?: Cadence;
  discount_enabled?: boolean;
  discount_percentage?: number;
  email_template?: string;
  engagement_points?: number;
  expires_after?: number;
  hide_payment_tiles?: boolean;
  image_file?: string;
  image_url?: string | null;
  interim_question_delay?: number;
  interim_question?: string;
  maximum_minutes_per_user?: number;
  maximum_redemptions_allowed?: number;
  minutes?: any;
  onboarding?: Onboarding;
  primary_question_timeout?: any;
  primary_question?: string;
  pto_reward_value?: any;
  rate?: number;
  required_points?: any;
  reward_value?: any;
  tremendous_campaign_id?: string;
  tremendous_organization_id?: string;
  is_call_units?: boolean;
  maximum_minutes_all_users?: any;
  component?: string;
}

export interface Cadence {
  on: string;
  period: string;
}

export interface Onboarding {
  delay: number;
  enabled?: boolean;
  system_message_id: any;
}

export interface Product {
  configuration: ProductConfiguration;
  description: string;
  id: number;
  key: string;
  name: string;
  product_type: string;
}

export interface ProductConfiguration {
  additional_discount?: number;
  auto_renew_frequency?: string;
  auto_renew_maximum_uses?: number;
  auto_renew?: boolean;
  cadence_time?: string;
  cadence?: Cadence;
  component: string;
  discount_enabled?: boolean;
  discount_percentage?: number;
  email_template: any;
  engagement_points?: number;
  expires_after: any;
  hide_payment_tiles?: boolean;
  image_file?: string;
  image_url?: string;
  interim_question_delay?: number;
  interim_question?: string;
  maximum_minutes_per_user?: number;
  maximum_redemptions_allowed?: number;
  minutes?: number;
  onboarding?: Onboarding;
  primary_question_timeout?: boolean;
  primary_question?: string;
  pto_reward_value?: number;
  rate?: number;
  required_points: any;
  reward_value?: string;
  tremendous_campaign_id?: string;
  tremendous_organization_id?: string;
}

export interface BrandingConfiguration {
  button_label: string;
  enabled: boolean;
  image_url?: string | null;
  placements: string[];
  type: string;
  url: string;
  video_url?: string;
}

export interface DependentsConfiguration {
  dependent_count: number;
  enabled: boolean;
  members_can_add_dependents: boolean;
}

export interface Subscription {
  banner_text: string;
  cents_per_minute: number;
  code: string;
  description?: string;
  details?: string;
  discount: number;
  id: number;
  minutes: number;
  name: string;
  price: number;
}

export interface VideoConfiguration {
  author_image_file?: any;
  author_image_url?: string;
  author_name?: string;
  enabled: boolean;
  image_file?: any;
  image_url?: string;
  title?: string;
  video_file?: string;
  video_url?: string;
}
export interface Vertical {
  id: number;
  key: string;
  name: string;
  description: string;
}

export interface Domain {
  id: number;
  key: string;
  name: string;
}

export interface CommsConfiguration {
  clever_webhook_process: boolean;
}

const getVerticals = async (): Promise<Vertical[]> => {
  return await axiosGet(`/verticals`, {}, 'v3').then((verticalResponse) => verticalResponse.data.data);
};

export const useVerticals = () => {
  return useQuery<Vertical[]>(['verticals'], () => getVerticals(), {});
};

const getDomains = async (): Promise<Domain[]> => {
  return await axiosGet(`/domains`, {}, 'v3').then((domainResponse) => domainResponse.data.data);
};

export const useDomains = () => {
  return useQuery<Domain[]>(['domains'], () => getDomains(), {});
};

const getClient = async (clientId: number): Promise<Client> => {
  return await axiosGet(`/clients/${clientId}/`, null, 'v2').then((clientResponse) => clientResponse.data);
};

export const useClient = (clientId?: number) => {
  const { data, isLoading, isFetching, error, refetch } = useQuery<Client>(
    ['clients', clientId],
    () => getClient(clientId!),
    {
      enabled: !!clientId,
    },
  );
  return { data, isLoading, isFetching, error, refetch };
};

const updateClientRequest = async (clientId: number, data: any): Promise<Client> => {
  return await axiosPut(`/clients/${clientId}`, data, 'v2').then((clientResponse) => clientResponse.data);
};

export interface CopyClientResourcesResult {
  target_client_id: number;
  source_client_id: number;
  copied_resources_count: number;
  deleted_resources_count: number;
  use_global_resources: boolean;
  enabled_resource_ids: number[];
}

const copyClientResourcesRequest = async (
  targetClientId: number,
  sourceClientId: number,
): Promise<CopyClientResourcesResult> => {
  return await axiosPost(`/clients/${targetClientId}/copy_resources`, { source_client_id: sourceClientId }, 'v3').then(
    (response) => response.data,
  );
};

export const useCopyClientResources = (targetClientId: number) => {
  const queryClient = useQueryClient();

  const copyClientResources = useMutation(
    (sourceClientId: number) => copyClientResourcesRequest(targetClientId, sourceClientId),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['clients', targetClientId]);
        toast.success(`Copied ${result.copied_resources_count} resource(s) and global selection from source client`);
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to copy client resources';
        console.error(error);
        toast.error(errorMessage);
      },
    },
  );

  return { copyClientResources };
};

export const useUpdateClient = (clientId: number, bypassOnSuccess: boolean = false) => {
  const queryClient = useQueryClient();
  const updateClient = useMutation((data: Partial<Client>) => updateClientRequest(clientId, data), {
    onSuccess: () => {
      if (!bypassOnSuccess) {
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Client updated successfully');
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries(['clients', clientId]);
      console.log(error);
    },
    onMutate: (data) => {
      queryClient.setQueryData(['clients', clientId], (oldData: any) => {
        if (oldData) {
          return { ...oldData, ...data };
        }
        return oldData;
      });
    },
  });

  return {
    updateClient,
  };
};

const createClientRequest = async (data: any): Promise<Client> => {
  return await axiosPost(`/clients/`, data, 'v2').then((clientResponse) => clientResponse.data);
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const createClient = useMutation((data: Partial<Client>) => createClientRequest(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(clientQueryKeys.listClients);
      toast.success('Client created successfully');
    },
    onError: (error) => {
      queryClient.invalidateQueries(clientQueryKeys.listClients);
      console.log(error);
    },
  });

  return {
    createClient,
  };
};

const getListClients = async (): Promise<Client[]> => {
  return await axiosGet(
    `/clients/?status=active&include_packages=true&order_by=name&order_by_direction=asc`,
    {},
    'v2',
  ).then((clientResponse) => clientResponse.data);
};

export const useListClients = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<Client[]>(clientQueryKeys.listClients, () =>
    getListClients(),
  );
  return { data, isLoading, error, refetch, isFetching };
};

const getClientsWithPackages = async (): Promise<Client[]> => {
  return await axiosGet(`/clients/?include_packages=true`, {}, 'v2').then((clientResponse) => clientResponse.data);
};

/*
  This query is used to get the Packages available to a Client administrator.,
  And we only want to fetch it if the user is a Client administrator.
*/
export const useClientsWithPackages = () => {
  const { roleType } = useAuth();
  return useQuery<Client[]>(clientQueryKeys.listClients, () => getClientsWithPackages(), {
    enabled: roleType === 'client_administrator',
  });
};

export interface ClientMinimal {
  id: number;
  name: string;
}

export interface ClientMinimalOptions {
  search?: string;
  show_all_clients?: boolean;
  is_test_client?: boolean;
  all_statuses?: boolean;
  /** Comma-separated statuses. Overrides type and all_statuses. */
  statuses?: string;
}

export interface ClientSummaryOptions extends ClientMinimalOptions {
  type?: string;
  /* CLJ: We're handling order filtering in the frontend due to the filter logic
  being under developed in the backend. The list of clients is short enough to handle it here in the frontend,
  but I'm leaving these fields here for future use if we decide to flesh out our backend filtering.
  */
  order_by?: string;
  order_by_direction?: string;
}

const mapClientTypeFilterToStatuses = (type: string) => {
  if (type.toLowerCase() === 'all') {
    return ['active', 'setup'];
  } else if (type.toLowerCase() === 'live') {
    return ['active'];
  } else if (type.toLowerCase() === 'setup') {
    return ['setup'];
  } else if (type.toLowerCase() === 'inactive') {
    return ['inactive'];
  } else return [];
};

const resolveClientStatuses = (options?: ClientSummaryOptions) => {
  if (options?.statuses) return options.statuses;
  if (options?.type) return mapClientTypeFilterToStatuses(options.type).toString();
  if (options?.all_statuses) return 'active,inactive,setup';
  return undefined;
};

const buildClientListParams = (view: 'summary' | 'minimal', options?: ClientSummaryOptions) => ({
  view,
  is_test_client: options?.is_test_client ?? false,
  show_all_clients: options?.show_all_clients ?? false,
  order_by: options?.order_by ?? 'name',
  order_by_direction: options?.order_by_direction ?? 'asc',
  search: options?.search ?? '',
  statuses: resolveClientStatuses(options),
});

const allClientsQueryConfig = {
  staleTime: 1000 * 60 * 15,
  cacheTime: 1000 * 60 * 15,
  refetchOnWindowFocus: false,
};

const getClientSummaryList = async (options?: ClientSummaryOptions): Promise<ClientSummary[]> => {
  // NOTE since summaries is not paginated we're pulling out the entire response.data array into return val here
  return await axiosGet(`/clients/`, buildClientListParams('summary', options), 'v2').then(
    (clientSummaryList) => clientSummaryList.data.data,
  );
};

/** The list endpoint defaults to 1000 rows. Dropdowns need every client. */
const MINIMAL_CLIENT_LIST_LIMIT = 10000;

const getClientMinimalList = async (options?: ClientMinimalOptions): Promise<ClientMinimal[]> => {
  const params = { ...buildClientListParams('minimal', options), limit: MINIMAL_CLIENT_LIST_LIMIT };
  return await axiosGet(`/clients/`, params, 'v2').then((response) => response.data.data);
};

export const useClientMinimalList = (options?: ClientMinimalOptions) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ClientMinimal[]>(
    [...clientQueryKeys.listClientMinimals, options ?? {}],
    () => getClientMinimalList(options),
    { refetchOnWindowFocus: false },
  );
  return { data, isLoading, error, refetch, isFetching };
};

export const useAllMinimalClients = (isTestClient: boolean = false) => {
  const options = { all_statuses: true, is_test_client: isTestClient };
  return useQuery<ClientMinimal[]>(
    [...clientQueryKeys.listClientMinimals, options],
    () => getClientMinimalList(options),
    allClientsQueryConfig,
  );
};

export const useClientSummaryList = (options?: ClientSummaryOptions) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ClientSummary[]>(
    [...clientQueryKeys.listClientSummaries, options ?? {}],
    () => getClientSummaryList(options),
    { refetchOnWindowFocus: false },
  );
  return { data, isLoading, error, refetch, isFetching };
};

export const useAllClientSummaries = (isTestClient: boolean = false) => {
  const options = { all_statuses: true, is_test_client: isTestClient };
  return useQuery<ClientSummary[]>(
    [...clientQueryKeys.allClientSummaries, options],
    () => getClientSummaryList(options),
    allClientsQueryConfig,
  );
};

export const useSyncHubspot = () =>
  useMutation((clientId: number) => axiosPost(`/clients/hubspot/`, { client_id: clientId }, 'v3'));

export const clientTypeFilterOptions = ['All', 'Setup', 'Live', 'Inactive'];

export const clientStatuses = ['active', 'setup', 'inactive', 'archived'];

export type ProductTemplateResponse = ProductTemplate[];

export enum ProductTemplateKey {
  CAPPED_ACCESS = 'CAPPED_ACCESS',
  CHECKIN_WITH_INTERIM = 'CHECKIN_WITH_INTERIM',
  DISCOUNT = 'DISCOUNT',
  REWARD_GIFT_CARD = 'REWARD_GIFT_CARD',
  REWARD_PTO = 'REWARD_PTO',
  SUBSCRIPTION = 'SUBSCRIPTION',
  UNLIMITED_ACCESS = 'UNLIMITED_ACCESS',
}

export interface ProductTemplate {
  configuration: Configuration;
  description: string;
  id: number;
  key: ProductTemplateKey;
  name: string;
  product_type: string;
}

const getProductTemplates = async (): Promise<ProductTemplateResponse> => {
  return await axiosGet(`/clients/product_templates`, {}, 'v2').then(
    (productTemplateResponse) => productTemplateResponse.data,
  );
};

export const useProductTemplates = () => {
  return useQuery<ProductTemplateResponse>(clientQueryKeys.productTemplates, () => getProductTemplates());
};

export type IncentiveOrganizationResponse = IncentiveOrganization[];

export interface IncentiveOrganization {
  text: string;
  value: string;
}

const getIncentiveOrganizations = async (): Promise<IncentiveOrganizationResponse> => {
  return await axiosGet(`/clients/incentive_organizations`, {}, 'v2').then(
    (incentiveOrganizationResponse) => incentiveOrganizationResponse.data,
  );
};

export const useIncentiveOrganizations = () => {
  return useQuery<IncentiveOrganizationResponse>(clientQueryKeys.incentiveOrganizations, () =>
    getIncentiveOrganizations(),
  );
};

const deletePackageRequest = async (clientId: number, packageId: number): Promise<any> => {
  return await axiosDelete(`/clients/${clientId}/packages/${packageId}`, {}, 'v2');
};

export const useDeletePackage = (clientId: number) => {
  const queryClient = useQueryClient();
  const deletePackage = useMutation(
    ({ packageId, packageName }: { packageId: number; packageName: string }) =>
      deletePackageRequest(clientId, packageId),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success(`${variables.packageName} deleted successfully`);
      },
      onError: (error) => {
        queryClient.invalidateQueries(['clients', clientId]);
        console.error(error);
      },
    },
  );

  return { deletePackage };
};

const createPackageRequest = async (clientId: number, data: Partial<Package>): Promise<Client> => {
  return await axiosPost(`/clients/${clientId}/packages`, data, 'v2').then((clientResponse) => clientResponse.data);
};

export const useCreatePackage = (clientId: number) => {
  const queryClient = useQueryClient();
  const createPackage = useMutation((data: Partial<Package>) => createPackageRequest(clientId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['clients', clientId]);
      toast.success('Package created successfully');
    },
    onError: (error) => {
      queryClient.invalidateQueries(['clients', clientId]);
      console.error(error);
    },
  });

  return { createPackage };
};

const updatePackageRequest = async (clientId: number, packageId: number, data: Partial<Package>): Promise<Client> => {
  return await axiosPut(`/clients/${clientId}/packages/${packageId}`, data, 'v2').then(
    (clientResponse) => clientResponse.data,
  );
};

export const useUpdatePackage = (clientId: number, packageId: number, bypassOnSuccess: boolean = false) => {
  const queryClient = useQueryClient();
  const updatePackage = useMutation((data: Partial<Package>) => updatePackageRequest(clientId, packageId, data), {
    onSuccess: () => {
      if (!bypassOnSuccess) {
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Package updated successfully');
      }
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['clients', clientId]);
      console.error(error);
      const errorMessage = error.response?.data?.description || 'Failed to update package';
      toast.error(errorMessage);
    },
  });

  return { updatePackage };
};

const createPackageProductRequest = async (
  clientId: number,
  packageId: number,
  productId: number,
  data: Partial<PackageProduct>,
): Promise<Client> => {
  return await axiosPost(`/clients/${clientId}/packages/${packageId}/products/${productId}`, data, 'v2').then(
    (clientResponse) => clientResponse.data,
  );
};

export const useCreatePackageProduct = (clientId: number, packageId: number, productId: number) => {
  const queryClient = useQueryClient();
  const createPackageProduct = useMutation(
    (data: Partial<PackageProduct>) => createPackageProductRequest(clientId, packageId, productId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Package product created successfully');
      },
      onError: (error: any) => {
        queryClient.invalidateQueries(['clients', clientId]);
        console.error(error);
        const errorMessage = error.response?.data?.description || 'Failed to create package product';
        toast.error(errorMessage);
      },
    },
  );

  return { createPackageProduct };
};

const updatePackageProductRequest = async (
  clientId: number,
  productId: number,
  data: Partial<PackageProduct>,
): Promise<Client> => {
  return await axiosPut(`/clients/${clientId}/package_product/${productId}`, data, 'v2').then(
    (clientResponse) => clientResponse.data,
  );
};

export const useUpdatePackageProduct = (clientId: number, productId: number) => {
  const queryClient = useQueryClient();
  const updatePackageProduct = useMutation(
    (data: Partial<PackageProduct>) => updatePackageProductRequest(clientId, productId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Package product updated successfully');
      },
      onError: (error: any) => {
        queryClient.invalidateQueries(['clients', clientId]);
        console.error(error);
        const errorMessage = error.response?.data?.description || 'Failed to update package product';
        toast.error(errorMessage);
      },
    },
  );

  return { updatePackageProduct };
};

const forceCheckInRequest = async (clientId: number, packageId: number, productId: number): Promise<Client> => {
  return await axiosPost(
    `/clients/${clientId}/packages/${packageId}/products/${productId}/force_check_in`,
    {},
    'v2',
  ).then((clientResponse) => clientResponse.data);
};

export const useForceCheckIn = (clientId: number, packageId: number, productId: number) => {
  const queryClient = useQueryClient();
  const forceCheckIn = useMutation(() => forceCheckInRequest(clientId, packageId, productId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['clients', clientId]);
      toast.success('Check-in forced successfully');
    },
    onError: (error) => {
      queryClient.invalidateQueries(['clients', clientId]);
      console.error(error);
    },
  });

  return { forceCheckIn };
};
